import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import JsPDF from 'jspdf';
import { toast } from 'react-toastify';
import { getSchools } from '../../API/apiService';
import DateRangeFilter from '../../components/dateRangeFilter';
import officialGeometry from './assets/medellin_official_geometry.json';
import {
  buildScopedGroupEntries,
  filterConfirmedSchools,
  isMedellinTerritoryId,
  normalizeAdminScope,
  resolveGroupMunicipalityId
} from '../../utils/adminScope';
import {
  clearDateFiltersFromSearchParams,
  filterSchoolsByGroupFirstMeetingDate,
  formatDateRangeLabel,
  getDateFiltersFromSearchParams,
  setDateFilterInSearchParams
} from '../../utils/dateFilters';
import { isGroupCancelled } from '../../utils/groupStatus';
import {
  drawPdfCard,
  drawPdfDistributionCard,
  formatPdfFileDate,
  formatPdfReportDate,
  svgElementToPngDataUrl
} from '../../utils/pdfReport';
import './style.scss';

const PROCESS_COPY = {
  general: {
    eyebrow: 'Analisis general de Cobertura',
    title: 'Escuelas de Participacion + Nada de Nervios',
    description:
      'Lectura territorial consolidada de los grupos creados en ambos procesos.'
  },
  pf: {
    eyebrow: 'Analisis de Cobertura',
    title: 'Escuelas de Participacion',
    description:
      'Lectura territorial del proceso con limites oficiales del Distrito para identificar que territorios concentran mas grupos y donde la cobertura sigue siendo baja.'
  },
  ndn: {
    eyebrow: 'Analisis de Cobertura',
    title: 'Nada de Nervios',
    description:
      'Lectura territorial del proceso con limites oficiales del Distrito para identificar que territorios concentran mas grupos y donde la cobertura sigue siendo baja.'
  },
  cs: {
    eyebrow: 'Analisis de Cobertura',
    title: 'Control Social',
    description:
      'Lectura territorial de los procesos formativos de Control Social registrados en el sistema.'
  },
  cop: {
    eyebrow: 'Analisis de Cobertura',
    title: 'Centro de Orientacion Pedagogica',
    description:
      'Lectura territorial de las asesorias COP registradas en el sistema.'
  },
  default: {
    eyebrow: 'Analisis de Cobertura',
    title: 'Proceso formativo',
    description:
      'Lectura territorial del proceso a partir de los grupos creados por territorio.'
  }
};

const PROCESS_REPORT_LABELS = {
  general: 'Escuelas de Participacion + Nada de Nervios',
  pf: 'Escuelas de Participacion',
  ndn: 'Nada de Nervios',
  cs: 'Control Social',
  cop: 'Centro de Orientacion Pedagogica'
};

const OFFICIAL_FEATURES = Array.isArray(officialGeometry?.features)
  ? officialGeometry.features
  : [];
const OFFICIAL_COMMUNES = OFFICIAL_FEATURES.filter(
  feature => feature.kind === 'commune'
).sort((left, right) => left.id - right.id);
const OFFICIAL_CORREGIMIENTOS = OFFICIAL_FEATURES.filter(
  feature => feature.kind === 'corregimiento'
).sort((left, right) => left.id - right.id);
const OFFICIAL_TERRITORIES = [...OFFICIAL_COMMUNES, ...OFFICIAL_CORREGIMIENTOS].sort(
  (left, right) => left.id - right.id
);

function formatNumber(value) {
  return new Intl.NumberFormat('es-CO').format(Number(value ?? 0));
}

function sortByName(left, right) {
  return left.name.localeCompare(right.name, 'es', {
    sensitivity: 'base'
  });
}

function sortByCountDesc(left, right) {
  if (right.count !== left.count) {
    return right.count - left.count;
  }

  return sortByName(left, right);
}

function sortByCountAsc(left, right) {
  if (left.count !== right.count) {
    return left.count - right.count;
  }

  return sortByName(left, right);
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function hexToRgb(hexColor) {
  const sanitized = hexColor.replace('#', '');
  return {
    red: parseInt(sanitized.slice(0, 2), 16),
    green: parseInt(sanitized.slice(2, 4), 16),
    blue: parseInt(sanitized.slice(4, 6), 16)
  };
}

function rgbToHex({ red, green, blue }) {
  return `#${[red, green, blue]
    .map(channel => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, '0'))
    .join('')}`;
}

function mixColor(startHex, endHex, progress) {
  const safeProgress = clamp(progress, 0, 1);
  const start = hexToRgb(startHex);
  const end = hexToRgb(endHex);

  return rgbToHex({
    red: start.red + (end.red - start.red) * safeProgress,
    green: start.green + (end.green - start.green) * safeProgress,
    blue: start.blue + (end.blue - start.blue) * safeProgress
  });
}

function getImpactFill(kind, count, maxCount) {
  if (count <= 0) {
    return kind === 'corregimiento' ? '#f2f4f6' : '#ffffff';
  }

  if (maxCount <= 1) {
    return kind === 'corregimiento' ? '#dbeef7' : '#d8eef8';
  }

  return kind === 'corregimiento'
    ? mixColor('#e7f1f6', '#acd9ed', count / maxCount)
    : mixColor('#e2f5fc', '#96d6ee', count / maxCount);
}

function getAccentColor(count, maxCount) {
  if (count <= 0) {
    return '#b7c0c9';
  }

  if (maxCount <= 1) {
    return '#1fa7d9';
  }

  return mixColor('#41bee8', '#0d7fae', count / maxCount);
}

function getBubbleRadius(count, maxCount) {
  if (count <= 0) {
    return 0;
  }

  if (maxCount <= 1) {
    return 16;
  }

  return 14 + ((count - 1) / (maxCount - 1)) * 10;
}

function buildTerritoryStats(countsByTerritory) {
  const maxCount = Math.max(
    ...OFFICIAL_TERRITORIES.map(territory => countsByTerritory[territory.id] || 0),
    0
  );

  return {
    maxCount,
    territoryStats: OFFICIAL_TERRITORIES.map(territory => {
      const count = countsByTerritory[territory.id] || 0;

      return {
        ...territory,
        count,
        fillColor: getImpactFill(territory.kind, count, maxCount),
        accentColor: getAccentColor(count, maxCount)
      };
    })
  };
}

function createEmptyCoverageSummary() {
  const { territoryStats, maxCount } = buildTerritoryStats({});

  return {
    totalGroups: 0,
    mappedGroups: 0,
    impactedTerritoriesCount: 0,
    noImpactTerritoriesCount: territoryStats.length,
    mostImpacted: null,
    lowImpactTerritories: [],
    topTerritories: [],
    noImpactTerritories: territoryStats.slice().sort((left, right) => left.id - right.id),
    territoryStats,
    maxCount
  };
}

function buildCoverageSummary(schools, scope) {
  const groupEntries = buildScopedGroupEntries(schools, scope)
    .filter(({ group }) => !isGroupCancelled(group));
  const groups = groupEntries.map(({ group }) => group);

  const countsByTerritory = groupEntries.reduce((accumulator, entry) => {
    const territoryId = resolveGroupMunicipalityId(entry.group, entry.school);

    if (isMedellinTerritoryId(territoryId)) {
      accumulator[territoryId] = (accumulator[territoryId] || 0) + 1;
    }

    return accumulator;
  }, {});

  const { territoryStats, maxCount } = buildTerritoryStats(countsByTerritory);
  const mappedGroups = territoryStats.reduce((sum, territory) => sum + territory.count, 0);
  const impactedTerritories = territoryStats
    .filter(territory => territory.count > 0)
    .sort(sortByCountDesc);
  const noImpactTerritories = territoryStats
    .filter(territory => territory.count === 0)
    .sort((left, right) => left.id - right.id);

  return {
    totalGroups: groups.length,
    mappedGroups,
    impactedTerritoriesCount: impactedTerritories.length,
    noImpactTerritoriesCount: noImpactTerritories.length,
    mostImpacted: impactedTerritories[0] || null,
    lowImpactTerritories: impactedTerritories
      .slice()
      .sort(sortByCountAsc)
      .slice(0, 5),
    topTerritories: impactedTerritories.slice(0, 5),
    noImpactTerritories,
    territoryStats,
    maxCount
  };
}

function SvgMultilineText({ x, y, lines, className, lineHeight }) {
  return (
    <text className={className} x={x} y={y} textAnchor='middle'>
      {lines.map((line, index) => (
        <tspan key={`${className}-${line}-${index}`} x={x} dy={index === 0 ? 0 : lineHeight}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

function SummaryCard({ label, value, helper, compact = false }) {
  return (
    <article className='admin-coverage-analysis__summary-card'>
      <span className='admin-coverage-analysis__summary-label'>{label}</span>
      <strong
        className={`admin-coverage-analysis__summary-value ${
          compact ? 'admin-coverage-analysis__summary-value--compact' : ''
        }`}>
        {value}
      </strong>
      <span className='admin-coverage-analysis__summary-helper'>{helper}</span>
    </article>
  );
}

function RankingCard({
  title,
  items,
  loading,
  maxCount,
  emptyMessage = 'Sin datos disponibles.'
}) {
  return (
    <article className='admin-coverage-analysis__insight-card'>
      <h2 className='admin-coverage-analysis__section-title'>{title}</h2>

      {loading ? (
        <p className='admin-coverage-analysis__empty'>Cargando datos...</p>
      ) : items.length > 0 ? (
        <div className='admin-coverage-analysis__ranking-list'>
          {items.map((item, index) => (
            <div
              key={`${title}-${item.id}`}
              className='admin-coverage-analysis__ranking-item'>
              <div className='admin-coverage-analysis__ranking-head'>
                <span>
                  {index + 1}. {item.name}
                </span>
                <strong>{formatNumber(item.count)}</strong>
              </div>
              <div className='admin-coverage-analysis__ranking-bar'>
                <span
                  style={{
                    width: `${
                      Math.max(
                        maxCount > 0 ? (item.count / maxCount) * 100 : 0,
                        item.count > 0 ? 8 : 0
                      )
                    }%`,
                    '--coverage-bar-color': item.accentColor
                  }}></span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className='admin-coverage-analysis__empty'>{emptyMessage}</p>
      )}
    </article>
  );
}

function EmptyCommunesCard({ items, loading }) {
  return (
    <article className='admin-coverage-analysis__insight-card'>
      <h2 className='admin-coverage-analysis__section-title'>
        Territorios sin grupos creados
      </h2>

      {loading ? (
        <p className='admin-coverage-analysis__empty'>Cargando datos...</p>
      ) : items.length > 0 ? (
        <div className='admin-coverage-analysis__chip-list'>
          {items.map(item => (
            <span
              key={`territory-empty-${item.id}`}
              className='admin-coverage-analysis__chip'>
              {item.id}. {item.name}
            </span>
          ))}
        </div>
      ) : (
        <p className='admin-coverage-analysis__empty'>
          Todos los territorios tienen al menos un grupo registrado.
        </p>
      )}
    </article>
  );
}

function CoverageMap({ territories, loading, maxCount, mostImpactedId, svgRef }) {
  const viewBoxWidth = officialGeometry?.viewBox?.width || 1040;
  const viewBoxHeight = officialGeometry?.viewBox?.height || 914;
  const communeTerritories = territories.filter(
    territory => territory.kind === 'commune'
  );
  const corregimientoTerritories = territories.filter(
    territory => territory.kind === 'corregimiento'
  );

  return (
    <div className='admin-coverage-analysis__map-shell'>
      <div className='admin-coverage-analysis__map-frame'>
        {loading ? (
          <p className='admin-coverage-analysis__empty'>
            Cargando distribucion territorial...
          </p>
        ) : (
          <svg
            ref={svgRef}
            className='admin-coverage-analysis__map map-graphic'
            viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
            role='img'
            aria-label='Mapa oficial de Medellin con cantidad de grupos creados por territorio'>
            {corregimientoTerritories.map(feature => (
              <path
                key={`corregimiento-${feature.id}`}
                className={`map-graphic__corregimiento ${
                  feature.count > 0 ? 'map-graphic__commune--active' : ''
                }`}
                d={feature.path}
                style={{
                  fill: feature.fillColor
                }}>
                <title>
                  {feature.id}. {feature.name}: {formatNumber(feature.count)} grupos
                </title>
              </path>
            ))}

            {communeTerritories.map(commune => (
              <path
                key={`commune-shape-${commune.id}`}
                className={`map-graphic__commune ${
                  commune.count > 0 ? 'map-graphic__commune--active' : ''
                }`}
                d={commune.path}
                style={{
                  fill: commune.fillColor
                }}>
                <title>
                  {commune.id}. {commune.name}: {formatNumber(commune.count)} grupos
                </title>
              </path>
            ))}

            {corregimientoTerritories.map(feature => (
              <SvgMultilineText
                key={`corregimiento-label-${feature.id}`}
                className='map-graphic__corregimiento-label'
                lineHeight={22}
                lines={feature.label.lines}
                x={feature.label.x}
                y={feature.label.y}
              />
            ))}

            {territories.map(territory => (
              <g key={`territory-label-${territory.id}`}>
                <text
                  className='map-graphic__label-id'
                  x={territory.label.x}
                  y={territory.label.y - 14}
                  textAnchor='middle'>
                  {territory.id}
                </text>

                <SvgMultilineText
                  className='map-graphic__label-name'
                  lineHeight={16}
                  lines={territory.label.lines}
                  x={territory.label.x}
                  y={territory.label.y}
                />

                {territory.count > 0 ? (
                  <g>
                    {territory.id === mostImpactedId ? (
                      <circle
                        className='map-graphic__bubble-ring'
                        cx={territory.bubble.x}
                        cy={territory.bubble.y}
                        r={getBubbleRadius(territory.count, maxCount) + 6}
                      />
                    ) : null}

                    <circle
                      className='map-graphic__bubble-shadow'
                      cx={territory.bubble.x}
                      cy={territory.bubble.y}
                      r={getBubbleRadius(territory.count, maxCount) + 2}
                    />

                    <circle
                      className='map-graphic__bubble'
                      cx={territory.bubble.x}
                      cy={territory.bubble.y}
                      r={getBubbleRadius(territory.count, maxCount)}
                      style={{
                        fill: territory.accentColor
                      }}
                    />

                    <text
                      className='map-graphic__bubble-text'
                      x={territory.bubble.x}
                      y={territory.bubble.y}>
                      {territory.count}
                    </text>
                  </g>
                ) : null}
              </g>
            ))}
          </svg>
        )}
      </div>

      <div className='admin-coverage-analysis__legend'>
        <span className='admin-coverage-analysis__legend-item'>
          <span className='admin-coverage-analysis__legend-swatch'></span>
          Geometria oficial del Distrito de Medellin.
        </span>
        <span className='admin-coverage-analysis__legend-item'>
          <span className='admin-coverage-analysis__legend-dot'></span>
          Cada burbuja muestra cuantos grupos tiene el territorio.
        </span>
        <span className='admin-coverage-analysis__legend-item'>
          <span className='admin-coverage-analysis__legend-dot admin-coverage-analysis__legend-dot--strong'></span>
          El anillo resalta el territorio con mayor impacto actual.
        </span>
      </div>
    </div>
  );
}

function AdminCoverageAnalysis() {
  const [searchParams, setSearchParams] = useSearchParams();
  const adminScope = normalizeAdminScope(searchParams.get('scope'));
  const dateFilters = getDateFiltersFromSearchParams(searchParams);
  const dateFrom = dateFilters.dateFrom;
  const dateTo = dateFilters.dateTo;
  const copy = PROCESS_COPY[adminScope] || PROCESS_COPY.default;
  const [summary, setSummary] = useState(createEmptyCoverageSummary);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const mapSvgRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function loadCoverageAnalysis() {
      setLoading(true);
      setError('');

      try {
        const schoolsResponse = await getSchools();

        if (!isMounted) {
          return;
        }

        const activeDateFilters = { dateFrom, dateTo };
        setSummary(
          buildCoverageSummary(
            filterSchoolsByGroupFirstMeetingDate(
              filterConfirmedSchools(schoolsResponse),
              activeDateFilters
            ),
            adminScope
          )
        );
      } catch (loadError) {
        console.error(loadError);
        if (!isMounted) {
          return;
        }

        setSummary(createEmptyCoverageSummary());
        setError('No fue posible cargar la cobertura territorial en este momento.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadCoverageAnalysis();

    return () => {
      isMounted = false;
    };
  }, [adminScope, dateFrom, dateTo]);

  function handleDateFilterChange(key, value) {
    setSearchParams(
      setDateFilterInSearchParams(searchParams, key, value),
      { replace: true }
    );
  }

  function handleClearDateFilters() {
    setSearchParams(clearDateFiltersFromSearchParams(searchParams), {
      replace: true
    });
  }

  const mappedGroupsHelper =
    summary.totalGroups === summary.mappedGroups
      ? 'Todos los grupos tienen un territorio valido dentro de Medellin.'
      : `${formatNumber(summary.mappedGroups)} de ${formatNumber(
          summary.totalGroups
        )} grupos aparecen ubicados en territorios oficiales del Distrito.`;
  const coverageShare =
    summary.impactedTerritoriesCount > 0
      ? `${formatNumber(summary.impactedTerritoriesCount)} de ${formatNumber(
          OFFICIAL_TERRITORIES.length
        )} territorios con presencia.`
      : 'Todavia no hay territorios con grupos registrados.';
  const mostImpactedLabel = summary.mostImpacted
    ? `${summary.mostImpacted.id}. ${summary.mostImpacted.name}`
    : '--';
  const mostImpactedHelper = summary.mostImpacted
    ? `${formatNumber(summary.mostImpacted.count)} grupos creados.`
    : 'Sin grupos registrados todavia.';

  async function handleDownloadAnalysis() {
    if (loading) {
      return;
    }

    setIsDownloading(true);

    try {
      const generatedAt = new Date();
      const processLabel =
        PROCESS_REPORT_LABELS[adminScope] || copy.title || 'Proceso formativo';
      const fileName = `analisis-cobertura-${adminScope || 'general'}-${formatPdfFileDate(
        generatedAt
      )}.pdf`;
      const doc = new JsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
      });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 30;
      const gutter = 12;
      const contentWidth = pageWidth - margin * 2;
      const summaryCardWidth = (contentWidth - gutter * 3) / 4;
      const summaryCardHeight = 84;
      const mapSourceWidth = officialGeometry?.viewBox?.width || 1040;
      const mapSourceHeight = officialGeometry?.viewBox?.height || 914;
      const mapAspectRatio = mapSourceWidth / mapSourceHeight;

      doc.setFillColor(237, 248, 251);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');

      doc.setFillColor(24, 57, 85);
      doc.roundedRect(margin, margin, contentWidth, 82, 24, 24, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      doc.setTextColor(255, 255, 255);
      doc.text(`Analisis de cobertura - ${processLabel}`, margin + 24, margin + 34);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(231, 244, 250);
      doc.text(
        `Reporte generado el ${formatPdfReportDate(generatedAt)}`,
        margin + 24,
        margin + 56
      );

      doc.setFontSize(9);
      doc.text(formatDateRangeLabel(dateFilters), margin + 24, margin + 72);

      const summaryCards = [
        {
          title: 'Grupos creados',
          value: formatNumber(summary.totalGroups),
          helper: mappedGroupsHelper
        },
        {
          title: 'Territorios impactados',
          value: formatNumber(summary.impactedTerritoriesCount),
          helper: coverageShare
        },
        {
          title: 'Territorio mas impactado',
          value: mostImpactedLabel,
          helper: mostImpactedHelper
        },
        {
          title: 'Territorios sin grupos',
          value: formatNumber(summary.noImpactTerritoriesCount),
          helper: 'Territorios por fortalecer en la cobertura'
        }
      ];

      summaryCards.forEach((card, index) => {
        const x = margin + index * (summaryCardWidth + gutter);
        drawPdfCard(doc, x, margin + 98, summaryCardWidth, summaryCardHeight, {
          ...card,
          valueSize: index === 2 ? 18 : 24
        });
      });

      const mapDataUrl = await svgElementToPngDataUrl(mapSvgRef.current, 1200);
      const mapBoxSize = Math.min(contentWidth, pageHeight - (margin + 194) - margin);
      const mapBoxX = margin + (contentWidth - mapBoxSize) / 2;
      const mapBoxY = margin + 194;
      const mapBoxWidth = mapBoxSize;
      const mapBoxHeight = mapBoxSize;
      const mapInnerPadding = 12;
      const mapTitleHeight = 34;
      const mapImageAvailableWidth = mapBoxWidth - mapInnerPadding * 2;
      const mapImageAvailableHeight =
        mapBoxHeight - mapTitleHeight - mapInnerPadding;
      let mapImageWidth = mapImageAvailableWidth;
      let mapImageHeight = mapImageWidth / mapAspectRatio;

      if (mapImageHeight > mapImageAvailableHeight) {
        mapImageHeight = mapImageAvailableHeight;
        mapImageWidth = mapImageHeight * mapAspectRatio;
      }

      const mapImageX = mapBoxX + (mapBoxWidth - mapImageWidth) / 2;
      const mapImageY =
        mapBoxY + mapTitleHeight + (mapImageAvailableHeight - mapImageHeight) / 2;

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(204, 233, 245);
      doc.roundedRect(mapBoxX, mapBoxY, mapBoxWidth, mapBoxHeight, 18, 18, 'FD');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(23, 50, 71);
      doc.text('Mapa de cobertura territorial', mapBoxX + 16, mapBoxY + 22);

      doc.addImage(
        mapDataUrl,
        'PNG',
        mapImageX,
        mapImageY,
        mapImageWidth,
        mapImageHeight
      );

      doc.addPage('a4', 'landscape');
      doc.setFillColor(237, 248, 251);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');

      doc.setFillColor(24, 57, 85);
      doc.roundedRect(margin, margin, contentWidth, 62, 20, 20, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text('Lecturas clave de cobertura territorial', margin + 20, margin + 38);

      const insightCardWidth = (contentWidth - gutter * 2) / 3;
      const insightCardHeight = 210;

      drawPdfDistributionCard(
        doc,
        margin,
        margin + 82,
        insightCardWidth,
        insightCardHeight,
        {
          title: 'Territorios que se estan impactando mas',
          items: summary.topTerritories.map(item => ({
            label: item.name,
            count: item.count
          })),
          accentColor: [24, 176, 229]
        }
      );

      drawPdfDistributionCard(
        doc,
        margin + insightCardWidth + gutter,
        margin + 82,
        insightCardWidth,
        insightCardHeight,
        {
          title: 'Territorios que se estan impactando menos',
          items: summary.lowImpactTerritories.map(item => ({
            label: item.name,
            count: item.count
          })),
          accentColor: [13, 127, 174]
        }
      );

      drawPdfCard(
        doc,
        margin + (insightCardWidth + gutter) * 2,
        margin + 82,
        insightCardWidth,
        insightCardHeight,
        {
          title: 'Territorios sin grupos creados',
          value: formatNumber(summary.noImpactTerritoriesCount),
          helper: summary.noImpactTerritories.length > 0
            ? summary.noImpactTerritories
              .map(item => `${item.id}. ${item.name}`)
              .join(', ')
            : 'Todos los territorios tienen al menos un grupo registrado.',
          valueSize: 24
        }
      );

      doc.save(fileName);
    } catch (downloadError) {
      console.error(downloadError);
      toast('No fue posible descargar el analisis de cobertura', {
        type: 'error'
      });
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <section className='admin-coverage-analysis'>
      <div className='admin-coverage-analysis__shell'>
        <div className='admin-coverage-analysis__toolbar'>
          <DateRangeFilter
            dateFrom={dateFilters.dateFrom}
            dateTo={dateFilters.dateTo}
            onChange={handleDateFilterChange}
            onClear={handleClearDateFilters}
            disabled={loading}
          />
          <button
            type='button'
            className='admin-coverage-analysis__download-button'
            onClick={handleDownloadAnalysis}
            disabled={loading || isDownloading}>
            <span className='material-symbols-rounded' aria-hidden='true'>
              download
            </span>
            {isDownloading ? 'Preparando archivo...' : 'Descargar analisis'}
          </button>
        </div>

        <section className='admin-coverage-analysis__hero'>
          <span className='admin-coverage-analysis__eyebrow'>
            {copy.eyebrow}
          </span>
          <h1 className='admin-coverage-analysis__title'>{copy.title}</h1>
          <p className='admin-coverage-analysis__description'>
            {copy.description}
          </p>
          {error ? (
            <p className='admin-coverage-analysis__error'>{error}</p>
          ) : null}
        </section>

        <section className='admin-coverage-analysis__summary-grid'>
          <SummaryCard
            label='Grupos creados'
            value={loading ? '--' : formatNumber(summary.totalGroups)}
            helper={loading ? 'Cargando grupos...' : mappedGroupsHelper}
          />
          <SummaryCard
            label='Territorios impactados'
            value={loading ? '--' : formatNumber(summary.impactedTerritoriesCount)}
            helper={loading ? 'Cargando cobertura...' : coverageShare}
          />
          <SummaryCard
            label='Territorio mas impactado'
            value={loading ? '--' : mostImpactedLabel}
            helper={loading ? 'Cargando impacto...' : mostImpactedHelper}
            compact
          />
          <SummaryCard
            label='Territorios sin grupos'
            value={loading ? '--' : formatNumber(summary.noImpactTerritoriesCount)}
            helper='Territorios por fortalecer en la cobertura.'
          />
        </section>

        <section className='admin-coverage-analysis__coverage-grid'>
          <article className='admin-coverage-analysis__map-card'>
            <div className='admin-coverage-analysis__card-header'>
              <h2 className='admin-coverage-analysis__section-title'>
                Mapa de cobertura territorial
              </h2>
              <p className='admin-coverage-analysis__text'>
                El mapa se renderiza con limites oficiales del Distrito de Medellin.
                Se resaltan los territorios con grupos creados y cada burbuja
                muestra la cantidad registrada en esa zona.
              </p>
            </div>

            <CoverageMap
              territories={summary.territoryStats}
              loading={loading}
              maxCount={summary.maxCount}
              mostImpactedId={summary.mostImpacted?.id}
              svgRef={mapSvgRef}
            />
          </article>

          <div className='admin-coverage-analysis__insights'>
            <RankingCard
              title='Territorios que se estan impactando mas'
              items={summary.topTerritories}
              loading={loading}
              maxCount={summary.maxCount}
              emptyMessage='Todavia no hay territorios con grupos registrados.'
            />

            <RankingCard
              title='Territorios que se estan impactando menos'
              items={summary.lowImpactTerritories}
              loading={loading}
              maxCount={summary.maxCount}
              emptyMessage='Todavia no hay suficientes grupos para comparar territorios.'
            />

            <EmptyCommunesCard
              items={summary.noImpactTerritories}
              loading={loading}
            />
          </div>
        </section>
      </div>
    </section>
  );
}

export default AdminCoverageAnalysis;
